import Vue from "vue";
import axios from "axios";
import { get, normalizeCharacters, getParameterByName } from "../utils";

const mockEndpoint = getParameterByName("endpoint");
const API_BASE_SRC = mockEndpoint || process.env.VUE_APP_API_BASE_SRC || "";
const ofertaCamp = _BBVA_MOTOR || "ofertaPersonales";
/**
 * Normalizar caracteres "especiales" de un payload
 * @param {Objet} payload Payload a enviar al back
 */
const normalizePayload = payload => {
  const payloadStr = JSON.stringify(payload);
  const payloadNormalizadoStr = normalizeCharacters(payloadStr);
  return JSON.parse(payloadNormalizadoStr);
};
/**
 * Método wrapper para promesas en uso con
 * async / await para evitar el uso de try / catch
 * y mejor manejo de errores
 * @param {Promise} promise Promesa a ejecutar
 * @returns Array | [error, data]
 * @description Convierte una promesa en un array con el error y el resultado
 */
export const to = promise => {
  return promise.then(data => [null, data]).catch(err => [err]);
};

/**
 * Método wrapper para reintentar llamados
 * en caso de error
 * @param {Promise} promise Promesa a ejecutar
 *  @param {Number} delay Tiempo de espera entre reintentos (en ms)
 *  @param {Number} retries Número de reintentos
 * @returns Array | [error, data]
 * @description Convierte una promesa en un array con el error y el resultado
 */
export const retry = async (promiseFn, delay = 2000, retries = 5) => {
  let attempt = 0;
  while (attempt <= retries) {
    try {
      const data = await promiseFn();
      return [null, data];
    } catch (err) {
      if (attempt === retries) {
        return [err];
      }
      if (delay > 0) {
        await new Promise(resolve => setTimeout(resolve, delay));
      }
      attempt++;
    }
  }
};

const om = {
  /**
   * Realizar request genérica a la API
   * @param {Object} payload payload para el endpoint
   */
  request({ endPoint, payload }) {
    // Siempre retornar la promesa de axios
    const body = payload ? JSON.parse(JSON.stringify(payload)) : payload;
    return axios.post(
      `${API_BASE_SRC}/prestamosPersonalesOM/${endPoint}`,
      body
    );
  },
  sucursales({ latitud, longitud }) {
    return axios.get(
      `${API_BASE_SRC}/pois/v0/branches?latitude=${latitud}&longitude=${longitud}&radius=5000000&branchType=INTEGRAL`
    );
  },
  merlin(payload, isBTD3) {
    let body = JSON.parse(JSON.stringify(payload));
    // El domicilio no puede tener caracteres "especiales"
    body.domicilio.calle = normalizeCharacters(body.domicilio.calle);
    body.domicilio.localidad = normalizeCharacters(body.domicilio.localidad);

    if (isBTD3) {
      Vue.$log.info("ENDPOINT BTD3");
      return axios.post(`${API_BASE_SRC}/ventaTC/wp/normalizarDomicilio`, body);
    } else {
      Vue.$log.info("ENDPOINT MERLIN");
      return axios.post(
        `${API_BASE_SRC}/ventaTC/wp/merlinCloud/normalizarDomicilio`,
        body
      );
    }
  },
  confirmacion(payload) {
    let body = {
      ...JSON.parse(JSON.stringify(payload)),
      c: `${ofertaCamp}`
    };
    return axios.post(
      `${API_BASE_SRC}/prestamosPersonalesOM/confirmarOferta`,
      body
    );
  },
  envioFrenteDni(payload) {
    return axios.post(`${API_BASE_SRC}/vdigital/wp/attachObverse`, payload);
  },
  envioReversoDni(payload) {
    return axios.post(`${API_BASE_SRC}/vdigital/wp/attachReverse`, payload);
  },
  envioSelfie(payload) {
    return axios.post(`${API_BASE_SRC}/vdigital/wp/attachSelfieAlive`, payload);
  },
  envioSelfieAlive(payload) {
    return axios.post(`${API_BASE_SRC}/vdigital/wp/attachProofOfLife`, payload);
  },
  getResultVeridas(
    key,
    nroTramiteOferta,
    consulta,
    numeroDoc,
    deviceId,
    deviceIdTimestamp,
    tipoDocumento
  ) {
    let body = {
      key: key,
      numeroTramite: nroTramiteOferta,
      nroDoc: get(consulta, "numeroDoc", ""),
      c: `${ofertaCamp}`,
      numeroCliente: numeroDoc,
      deviceId,
      deviceIdTimestamp,
      tipoDocumento
    };
    return axios.post(`${API_BASE_SRC}/vdigital/wp/getResult`, body);
  },
  loginVeridas(payload) {
    return axios.post(`${API_BASE_SRC}/vdigital/wp/login`, payload);
  },
  getContract(payload) {
    let body = JSON.parse(JSON.stringify(payload));
    return axios.post(`${API_BASE_SRC}/ventaTC/wp/consultarContratos`, body);
  },
  downloadContract(payload) {
    let body = JSON.parse(JSON.stringify(payload));
    return axios.post(`${API_BASE_SRC}/ventaTC/wp/descargarContratos`, body);
  },
  signContracts(payload) {
    let body = JSON.parse(JSON.stringify(payload));
    return axios.post(`${API_BASE_SRC}/ventaTC/wp/firmarContratos`, body);
  },
  generarOtp(payload) {
    let body = JSON.parse(JSON.stringify(payload));
    return axios.post(`${API_BASE_SRC}/ventaTC/wp/generarOtp`, body);
  },
  validarOtp(payload) {
    let body = JSON.parse(JSON.stringify(payload));
    return axios.post(`${API_BASE_SRC}/ventaTC/wp/validarOtp`, body);
  },
  crearUsuario(payload) {
    let body = JSON.parse(JSON.stringify(payload));
    return axios.post(`${API_BASE_SRC}/ventaTC/wp/usuarioYClave`, body);
  },
  normalizarCelular(payload) {
    let body = {
      level1: "AR",
      level2: "",
      level3: "",
      level4: "",
      level5: "",
      phoneNumber: payload,
      characteristic: "",
      postalCode: "",
      prefix: "",
      additionalData: ""
    };
    return axios.post(`${API_BASE_SRC}/ventaTC/wp/normalizarTelefono`, body);
  },
  certificarCelular(payload) {
    const body = normalizePayload(payload);
    return axios.post(
      `${API_BASE_SRC}/aperturaCA/wp/certificarCelularCliente`,
      body
    );
  },
  acortarUrl(url) {
    return axios.post(`${API_BASE_SRC}/utils/sUrl`, { url });
  },
  tokenizarUser(payload) {
    return axios.post(`${API_BASE_SRC}/customer/tokenize`, payload);
  },
  enviarSms(payload) {
    let body = JSON.parse(JSON.stringify(payload));
    return axios.post(`${API_BASE_SRC}/biometriaEnCA/wp/enviarSmsSeguro`, body);
  },
  consultaLineasPH() {
    return axios.post(
      `${API_BASE_SRC}/prestamosHipotecarios/consultarLineasDisponibles`
    );
  },
  consultaPH(payload) {
    return axios.post(
      `${API_BASE_SRC}/prestamosHipotecarios/consultaPH`,
      payload
    );
  },
  confirmarPH(payload) {
    return axios.post(
      `${API_BASE_SRC}/prestamosHipotecarios/confirmarPH`,
      payload
    );
  },
  enviarRenaper(payload) {
    let body = JSON.parse(JSON.stringify(payload));
    return axios.post(`${API_BASE_SRC}/ventaTC/wp/confirmarDatos`, body);
  },
  fechaSistema() {
    return axios.get(`${API_BASE_SRC}/utils/fechasistema`);
  },
  cotizacionMonedaExtranjera() {
    return axios.get(`${API_BASE_SRC}/cotizaciones/monedaExtranjera`);
  },
  enviarResultadosBiometria(payload) {
    let body = JSON.parse(JSON.stringify(payload));
    return axios.post(`${API_BASE_SRC}/dvs/wp/verificationProcess`, body);
  },
  consultarReintentosBio(payload) {
    return axios.get(`${API_BASE_SRC}/dvs/wp/countOfRecords?doc=${payload}`);
  },
  enviarEventoDvsFS(payload) {
    let body = JSON.parse(JSON.stringify(payload));
    return axios.post(`${API_BASE_SRC}/dvs/wp/enviarEventoFS`, body);
  },
  getPrescriptor(payload) {
    return axios.get(
      `${API_BASE_SRC}/prestamosHipotecarios/datosPrescriptores?codigoPrescriptor=${payload}`
    );
  },
  getSimulacionInput(payload) {
    let body = JSON.parse(JSON.stringify(payload));
    return axios.post(
      `${API_BASE_SRC}/prestamosHipotecarios/getSimulacionInput`,
      body
    );
  },
  obtenerUriPorUid(uid) {
    return axios.get(`${API_BASE_SRC}/utils/sUrl/url/${uid}`);
  },
  decryptHrDatos(params) {
    return axios.get(`${API_BASE_SRC}/alprestamo/decrypt`, { params });
  }
};

export default om;
